import { Phone } from 'iconoir-react'
import { ReactElement } from 'react'
import { ActivityDrawer } from 'app/components/activities/ActivityDrawer'
import SimpleTable from 'app/components/table/SimpleTable'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { AiCallDetailsDrawer } from './AiCallDetailsDrawer'
import { CallFailuresSection } from './CallFailuresSection'
import { DippingScheduleRatesSection } from './DippingScheduleRatesSection'
import { NoBookingReasonsSection } from './NoBookingReasonsSection'
import { OpsTrendGraph, type TrendSeries } from './OpsTrendGraph'
import { SectionTitle } from './SectionTitle'
import SegmentControl from 'app/components/viz/SegmentControl'
import { SlippingOverTimeSection } from './SlippingOverTimeSection'
import { StatsRow } from './StatsRow'
import type { FlagFilter, UseVoiceAgentOpsReturn } from './useVoiceAgentOps'

const AI_CALLS_SERIES: TrendSeries[] = [
  { key: `Calls`, color: `green`, value: (totals) => totals.aiCalls - totals.failedCalls },
  { key: `Tool Failures`, color: `red`, value: (totals) => totals.failedCalls },
]

const FLAG_FILTER_OPTIONS: { value: FlagFilter; label: string }[] = [
  { value: `all`, label: `All` },
  { value: `toolError`, label: `Errors` },
  { value: `toolTimeout`, label: `Timeouts` },
  { value: `toolDuplicate`, label: `Duplicates` },
]

/**
 * The Ops tab, top to bottom (plan D41): cards → AI Calls → Location Breakdown →
 * Dipping Schedule Rates → No Booking Reasons → Slipping Over Time → Tool Failures →
 * ElevenLabs & GPT Failures. Tables grow with their rows; the page does the scrolling.
 */
export function VoiceAgentOpsComponent(props: UseVoiceAgentOpsReturn): ReactElement {
  const {
    fromDate,
    toDate,
    isLoading,
    summary,
    performanceStats,
    failureStats,
    daily,
    locationRows,
    locationColumns,
    failedCalls,
    failedCallColumns,
    flagFilter,
    setFlagFilter,
    flagCounts,
    callListRef,
    drawer,
    loadActivity,
    callDetail,
    selectedCallLocationName,
    openSelectedCallActivity,
  } = props
  const highlightedCallId = callDetail.selectedCall?.id ?? drawer.focusedActivity?.id

  return (
    <>
      <div className="space-y-3 md:space-y-4">
        <StatsRow stats={performanceStats} isLoading={isLoading} skeletonCount={5} />
        <StatsRow stats={failureStats} isLoading={isLoading} skeletonCount={4} />
      </div>

      <div className="mt-8">
        <OpsTrendGraph
          title="AI Calls"
          badge={`${summary.aiCalls.toLocaleString(`en-US`)} Calls`}
          series={AI_CALLS_SERIES}
          daily={daily}
          fromDate={fromDate}
          toDate={toDate}
          isLoading={isLoading}
        />
      </div>

      <div className="mb-8">
        <div className="mb-3">
          <SectionTitle title="Location Breakdown" badge={`${locationRows?.length ?? 0} Locations`} />
        </div>
        {/* No height on the wrapper: the table grows with its rows and the page does the
            scrolling. A fixed height makes the table its own scroll box, which catches the
            wheel mid-page. */}
        <SimpleTable
          activeSubpage="voice-agent-ops-locations"
          data={locationRows}
          columns={locationColumns}
          initialSort={[{ id: `aiCalls`, desc: true }]}
          enableDeepLinking={false}
          emptyState={
            <TableEmptyState
              icon={<Phone className="h-6 w-6" strokeWidth={1.5} />}
              title="No AI Agent locations"
              description="Locations with the AI Agent product show up here with their call and tool failure stats."
            />
          }
        />
      </div>

      <DippingScheduleRatesSection {...props} />

      <NoBookingReasonsSection {...props} highlightedCallId={highlightedCallId} />

      <SlippingOverTimeSection {...props} />

      <div ref={callListRef} className="mb-8 scroll-mt-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-y-2">
          <SectionTitle title="Tool Failures" badge={`${flagCounts.all} Calls`} />
          {/* Scrolls on its own on a phone rather than pushing the page sideways. */}
          <div className="max-w-full overflow-x-auto">
            <SegmentControl
              label="Error type"
              buttons={FLAG_FILTER_OPTIONS.map((option) => ({
                label: `${option.label} ${flagCounts[option.value]}`,
                onClick: () => setFlagFilter(option.value),
                isActive: flagFilter === option.value,
              }))}
            />
          </div>
        </div>
        <SimpleTable
          activeSubpage="voice-agent-ops-failed-calls"
          data={failedCalls}
          columns={failedCallColumns}
          enableDeepLinking={false}
          hideFooter
          meta={{ getRowStyle: (row) => (row?.id === highlightedCallId ? `table-selected-row` : ``) }}
          emptyState={
            <TableEmptyState
              icon={<Phone className="h-6 w-6" strokeWidth={1.5} />}
              title="No calls with tool failures"
              description="Calls where the agent's tools errored, timed out, or repeated show up here."
            />
          }
        />
      </div>

      <CallFailuresSection {...props} highlightedCallId={highlightedCallId} />

      <AiCallDetailsDrawer
        call={callDetail.selectedCall}
        callData={callDetail.callData}
        isLoading={callDetail.isCallDataLoading}
        locationName={selectedCallLocationName}
        onClose={callDetail.closeCall}
        onOpenActivity={openSelectedCallActivity}
      />
      <ActivityDrawer
        activity={drawer.focusedActivity}
        isLoading={drawer.isActivityLoading}
        open={drawer.openDrawer}
        onChange={loadActivity}
        onClose={drawer.closeDrawer}
      />
    </>
  )
}
